<template>
  <Transition name="notifications-preview-slide">
    <div
      v-if="previewOpen"
      class="notifications-preview"
      :class="{ 'notifications-preview--below-project': belowProjectHeader }"
    >
      <button
        type="button"
        class="notifications-preview__backdrop"
        aria-label="Cerrar notificaciones"
        @click="close"
      />
      <div class="notifications-preview__panel" role="dialog" aria-label="Notificaciones sin ver">
        <header class="notifications-preview__header">
          <div class="notifications-preview__title">Notificaciones</div>
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            aria-label="Cerrar notificaciones"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <div class="notifications-preview__body">
          <GuestRestrictionMessage
            v-if="isGuestUser"
            feature="notifications"
            compact
            :show-back-button="false"
          />
          <div v-else-if="unreadListLoading && unreadItems.length === 0" class="notifications-preview__status">
            <v-progress-circular indeterminate color="primary" size="28" width="2" />
          </div>
          <v-alert
            v-else-if="unreadListError"
            type="error"
            variant="tonal"
            density="compact"
            class="ma-3"
          >
            {{ unreadListError }}
          </v-alert>
          <div
            v-else-if="!loading && !configured"
            class="notifications-preview__status text-body-2 text-medium-emphasis"
          >
            Las notificaciones aún no están configuradas en este entorno.
          </div>
          <div
            v-else-if="unreadItems.length === 0"
            class="notifications-preview__status text-body-2 text-medium-emphasis"
          >
            No tienes notificaciones sin ver.
          </div>
          <div v-else class="notifications-preview__unread">
            <div class="notifications-preview__mark">
              <v-btn
                variant="text"
                color="primary"
                :loading="markingAll"
                @click="onMarkAllSeen"
              >
                Marcar como vistas
              </v-btn>
            </div>
            <v-list class="notifications-preview__list" density="compact">
              <v-list-item
                v-for="item in unreadItems"
                :key="item.id"
                class="notifications-preview__item"
                @click="onItemClick(item)"
              >
                <template #prepend>
                  <v-avatar color="primary" size="32">
                    <v-icon color="white" size="18">{{ iconFor(item) }}</v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="text-wrap">
                  {{ item.subject || 'Notificación' }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ item.body }}
                </v-list-item-subtitle>
                <span
                  v-if="item.createdAt"
                  class="notifications-preview__time"
                >
                  {{ formatRelative(item.createdAt) }}
                </span>
              </v-list-item>
            </v-list>
          </div>
        </div>

        <ColumnButtons
          v-if="!isGuestUser"
          class="notifications-preview__see-all"
          primary-text="Ver todas"
          :show-secondary="false"
          @primary-click="goToAll"
        />
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import type { Notification } from '@novu/js'

import ColumnButtons from '@/components/baseComponents/ColumnButtons.vue'
import GuestRestrictionMessage from '@/components/baseComponents/GuestRestrictionMessage.vue'
import { useAuthStore } from '@/stores/auth'
import { useNotificationsStore } from '@/stores/notifications'
import { getNotificationRedirectUrl } from '@/utils/notificationRedirect'

export default defineComponent({
  name: 'NotificationsPreview',

  components: {
    ColumnButtons,
    GuestRestrictionMessage,
  },

  setup() {
    const route = useRoute()
    const router = useRouter()
    const isMobile = useDisplay().mobile
    const authStore = useAuthStore()
    const notificationsStore = useNotificationsStore()
    const markingAll = ref(false)
    const isGuestUser = computed(() => authStore.userType === 'guest')
    const belowProjectHeader = computed(() => !isMobile.value && route.name === 'project')
    const {
      previewOpen,
      configured,
      loading,
      unreadItems,
      unreadListLoading,
      unreadListError,
    } = storeToRefs(notificationsStore)

    const close = () => {
      notificationsStore.closePreview()
    }

    const goToAll = async () => {
      notificationsStore.closePreview()
      await router.push({ name: 'notifications' })
    }

    const onMarkAllSeen = async () => {
      markingAll.value = true
      try {
        await notificationsStore.markAllAsRead()
        await notificationsStore.loadUnread()
      } finally {
        markingAll.value = false
      }
    }

    const onItemClick = async (item: Notification) => {
      await notificationsStore.markAsRead(item.id)
      const redirectUrl = getNotificationRedirectUrl(item)
      if (!redirectUrl) {
        await notificationsStore.loadUnread()
        return
      }
      notificationsStore.closePreview()
      if (redirectUrl.startsWith('/')) {
        await router.push(redirectUrl)
        return
      }
      if (typeof window !== 'undefined') {
        window.location.assign(redirectUrl)
      }
    }

    const formatRelative = (createdAt?: string | Date) => {
      if (!createdAt) return ''
      return formatDistanceToNow(new Date(createdAt), {
        addSuffix: true,
        locale: es,
      })
    }

    const iconFor = (item: Notification) => {
      const workflow = item.workflow?.identifier || ''
      if (workflow.includes('comment')) return 'mdi-comment-text-outline'
      if (workflow.includes('follow')) return 'mdi-account-plus-outline'
      if (workflow.includes('group')) return 'mdi-account-group-outline'
      if (workflow.includes('streak')) return 'mdi-fire'
      if (workflow.includes('protector')) return 'mdi-shield-check-outline'
      if (workflow.includes('star')) return 'mdi-star-outline'
      if (workflow.includes('challenge')) return 'mdi-flag-checkered'
      if (workflow.includes('writing-habit') || workflow.includes('writing_habit')) {
        return 'mdi-chart-box-outline'
      }
      return 'mdi-bell-outline'
    }

    return {
      previewOpen,
      configured,
      loading,
      unreadItems,
      unreadListLoading,
      unreadListError,
      isGuestUser,
      belowProjectHeader,
      markingAll,
      close,
      goToAll,
      onMarkAllSeen,
      onItemClick,
      formatRelative,
      iconFor,
    }
  },
})
</script>

<style scoped>
.notifications-preview {
  position: fixed;
  top: 58px;
  right: 16px;
  z-index: 2200;
  width: min(400px, calc(100vw - 24px));
  pointer-events: none;
}

.notifications-preview::before {
  content: '';
  position: absolute;
  inset: -48px -36px -32px;
  z-index: 0;
  border-radius: 48px;
  background: radial-gradient(
    ellipse 90% 80% at 50% 55%,
    rgba(var(--v-theme-primary), 0.4) 0%,
    rgba(var(--v-theme-primary), 0.18) 45%,
    transparent 74%
  );
  filter: blur(26px);
  opacity: 0.95;
  pointer-events: none;
}

.notifications-preview--below-project {
  top: 112px;
}

.notifications-preview__backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: default;
  pointer-events: auto;
}

.notifications-preview__panel {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  max-height: min(520px, calc(100vh - 80px));
  background: rgb(var(--v-theme-surface));
  border-radius: 16px;
  box-shadow:
    0 0 0 1px rgba(var(--v-theme-primary), 0.08),
    0 0 32px rgba(var(--v-theme-primary), 0.28),
    0 0 64px rgba(var(--v-theme-primary), 0.14),
    0 2px 8px rgba(0, 0, 0, 0.06),
    0 16px 40px rgba(0, 0, 0, 0.14),
    0 32px 64px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.notifications-preview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 52px;
  padding: 8px 8px 8px 16px;
  background: rgb(var(--v-theme-primary));
  color: white;
}

.notifications-preview__title {
  font-weight: 600;
}

.notifications-preview__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.notifications-preview__status {
  padding: 28px 16px;
  text-align: center;
}

.notifications-preview__unread {
  display: flex;
  flex-direction: column;
}

.notifications-preview__mark {
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px 0;
}

.notifications-preview__list {
  background: transparent;
  padding: 0;
}

.notifications-preview__item {
  cursor: pointer;
}

.notifications-preview__time {
  display: block;
  margin-top: 2px;
  font-size: 0.7rem;
  line-height: 1.2;
  font-weight: 400;
  color: rgba(var(--v-theme-on-surface), 0.42);
}

.notifications-preview__see-all {
  padding: 0 16px 8px;
}

.notifications-preview-slide-enter-active,
.notifications-preview-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.notifications-preview-slide-enter-from,
.notifications-preview-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
